/** Rubrique IA · OCR et traduction : fournisseurs séparés, recherche web, clés API perso. */
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { KeyRound, Trash2, CheckCircle2 } from 'lucide-react';
import { metaApi, parametresApi } from '../../../services/api';
import { Button, Spinner, Card } from '../../../components/ui/kit';
import { useAutoSave, AutoSaveStatus } from '../../../hooks/useAutoSave';

const PROVIDERS = [{ value: 'anthropic', label: 'Claude (Anthropic)' }, { value: 'openai', label: 'ChatGPT (OpenAI)' }];

export default function IASettings({ api }) {
  // Adaptateur de source : établissement par défaut, ou super-admin (config globale) via prop `api`.
  const a = api || {
    load: () => metaApi.settings(),
    save: (v) => parametresApi.updateEstablishment(v),
    saveKey: (p, k) => parametresApi.saveAiKey(p, k),
    deleteKey: (p) => parametresApi.deleteAiKey(p),
    showPlaces: true,
    title: 'IA · OCR et traduction',
  };
  const [est, setEst] = useState(null);
  const load = useCallback(() => a.load().then(setEst), [a]);
  useEffect(() => { load(); }, [load]);
  const status = useAutoSave(est, (v) => (v ? a.save(v) : null));
  if (!est) return <Spinner />;
  const set = (k, v) => setEst({ ...est, [k]: v });

  return (
    <div className="max-w-[560px]">
      <div className="flex items-center gap-3 mb-5">
        <h2 className="font-title text-3xl">{a.title || 'IA · OCR et traduction'}</h2>
        <AutoSaveStatus status={status} />
      </div>
      <Card className="p-5 space-y-4">
        <Sel label="Fournisseur par défaut (général)" value={est.ai_provider || 'anthropic'} onChange={(v) => set('ai_provider', v)} testId="ia-provider" />
        <Sel label="Fournisseur OCR (étiquettes, factures)" value={est.ai_ocr_provider || est.ai_provider || 'anthropic'} onChange={(v) => set('ai_ocr_provider', v)} testId="ia-ocr" />
        <Sel label="Fournisseur traduction" value={est.ai_translation_provider || est.ai_provider || 'anthropic'} onChange={(v) => set('ai_translation_provider', v)} testId="ia-translation" />
        <label className="flex items-center gap-2 text-[13.5px] cursor-pointer">
          <input type="checkbox" className="accent-gold" checked={est.web_search_enabled ?? true} onChange={(e) => set('web_search_enabled', e.target.checked)} data-testid="ia-websearch" />
          Recherche web activée (enrichissement des fiches)
        </label>
        {est.ai_calls_month != null && (
          <div className="hair-t pt-3">
            <p className="kicker mb-1">Journal de consommation (mois)</p>
            <p className="text-[13px] text-n600">Appels : {est.ai_calls_month ?? 0} · Coût estimé : {(est.ai_cost_month ?? 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' })}</p>
          </div>
        )}
      </Card>

      <Card className="p-5 space-y-4 mt-5" data-testid="ia-keys-panel">
        <div>
          <h3 className="font-title text-2xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-gold700" /> Clés API personnelles</h3>
          <p className="text-[13px] text-n600 mt-1">
            Renseignez vos propres clés OpenAI / Claude. L'application n'utilise QUE ces clés configurées
            (établissement en priorité, sinon super-admin) ; sans clé, les fonctions IA sont désactivées.
          </p>
        </div>
        <KeyManager api={a} provider="openai" label="Clé OpenAI (sk-…)" placeholder="sk-…"
          configured={est.ai_openai_key_set} last4={est.ai_openai_key_enc_last4} onChanged={load} />
        <KeyManager api={a} provider="anthropic" label="Clé Anthropic / Claude (sk-ant-…)" placeholder="sk-ant-…"
          configured={est.ai_anthropic_key_set} last4={est.ai_anthropic_key_enc_last4} onChanged={load} />
      </Card>

      {a.showPlaces !== false && <PlacesCard />}
    </div>
  );
}

/** Carte de configuration de la clé Google Places (facultative, repli OpenStreetMap). */
function PlacesCard() {
  const [status, setStatus] = useState(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => parametresApi.integrationsStatus().then((s) => setStatus(s.google_places)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try { await parametresApi.savePlacesKey(value.trim()); toast.success('Clé Google Places enregistrée'); setValue(''); load(); }
    catch (e) { toast.error(e.message || 'Clé refusée'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await parametresApi.deletePlacesKey(); toast.success('Clé supprimée'); load(); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 space-y-4 mt-5" data-testid="places-key-panel">
      <div>
        <h3 className="font-title text-2xl flex items-center gap-2"><KeyRound className="w-5 h-5 text-gold700" /> Google Places (facultatif)</h3>
        <p className="text-[13px] text-n600 mt-1">
          Enrichit les liens Google Maps de « À découvrir » (adresse, site, téléphone). Sans clé,
          l'application utilise l'extraction depuis l'URL et le géocodage gratuit OpenStreetMap.
        </p>
      </div>
      <div className="hair-t pt-4" data-testid="places-key">
        <p className="kicker mb-1">Clé API Google Places</p>
        {status?.configured ? (
          <div className="flex items-center justify-between gap-3">
            <span className="text-[13.5px] flex items-center gap-2" data-testid="places-key-status">
              <CheckCircle2 className="w-4 h-4 text-gold700" /> Configurée · se termine par <span className="nums">••••{status.last4 || '••••'}</span>
            </span>
            <Button variant="secondary" onClick={remove} disabled={busy} data-testid="places-key-delete"><Trash2 className="w-4 h-4" /> Supprimer</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <input type="password" autoComplete="off" className="vd-input flex-1" placeholder="AIza…" value={value} onChange={(e) => setValue(e.target.value)} data-testid="places-key-input" />
            <Button variant="primary" onClick={save} disabled={busy || !value.trim()} data-testid="places-key-save">Enregistrer</Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function KeyManager({ api, provider, label, placeholder, configured, last4, onChanged }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      await api.saveKey(provider, value.trim());
      toast.success('Clé enregistrée (chiffrée)');
      setValue('');
      onChanged();
    } catch (e) { toast.error(e.message || 'Clé refusée'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.deleteKey(provider); toast.success('Clé supprimée'); onChanged(); }
    catch (e) { toast.error(e.message || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div className="hair-t pt-4" data-testid={`ia-key-${provider}`}>
      <p className="kicker mb-1">{label}</p>
      {provider === 'openai' && (
        <p className="text-[12px] text-n500 mb-2" data-testid="ia-key-openai-billing">
          Pour suivre &amp; créditer OpenAI :{' '}
          <a href="https://platform.openai.com/settings/organization/billing/overview" target="_blank" rel="noopener noreferrer" className="vd-link">
            https://platform.openai.com/settings/organization/billing/overview
          </a>
        </p>
      )}
      {provider === 'anthropic' && (
        <p className="text-[12px] text-n500 mb-2" data-testid="ia-key-anthropic-billing">
          Pour suivre &amp; créditer Claude :{' '}
          <a href="https://platform.claude.com/settings/keys" target="_blank" rel="noopener noreferrer" className="vd-link">
            https://platform.claude.com/settings/keys
          </a>
        </p>
      )}
      {configured ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-[13.5px] flex items-center gap-2" data-testid={`ia-key-${provider}-status`}>
            <CheckCircle2 className="w-4 h-4 text-gold700" /> Configurée · se termine par <span className="nums">••••{last4 || '••••'}</span>
          </span>
          <Button variant="secondary" onClick={remove} disabled={busy} data-testid={`ia-key-${provider}-delete`}>
            <Trash2 className="w-4 h-4" /> Supprimer
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input type="password" autoComplete="off" className="vd-input flex-1" placeholder={placeholder}
            value={value} onChange={(e) => setValue(e.target.value)} data-testid={`ia-key-${provider}-input`} />
          <Button variant="primary" onClick={save} disabled={busy || !value.trim()} data-testid={`ia-key-${provider}-save`}>
            Enregistrer
          </Button>
        </div>
      )}
    </div>
  );
}

function Sel({ label, value, onChange, testId }) {
  return (
    <label className="block"><span className="kicker block mb-1">{label}</span>
      <select className="vd-input h-9 w-full" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
      </select>
    </label>
  );
}
